/**
 * External Imports
 */
import { FC, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import {
  useGetMasterTemplatesQuery,
  useGetQuestionsQuery
} from '../../../../redux/apis/Templates.api';

import { useUserRolesRef } from '../../../../services/UserRoles';

import { useDebounceValue } from '../../../../utils/debouncers';
import { formatRoundingType, toTitleCase } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  CleanQuestion,
  ICondensedMasterTemplate,
  ICondensedQuestion,
  Question,
  QuestionOptionProp,
  QuestionPartProp
} from '../../../../config/interfaces';

import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';

interface QuestionBankTableProps {
  searchTerm: string;
  handleEditQuestion: (id: string) => void;
  setCsvData: (data: CleanQuestion[]) => void;
}
const QuestionBankTable: FC<QuestionBankTableProps> = ({
  searchTerm,
  handleEditQuestion,
  setCsvData
}) => {
  const [cleanQuestions, setCleanQuestions] = useState<CleanQuestion[]>([]);
  const [sortKey, setSortKey] = useState<string>('questionTitle');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Title');

  const { isEnterpriseAdmin } = useUserRolesRef();

  // debouncing search terms
  const debouncedQuestionSearchTerm = useDebounceValue(searchTerm, 275);

  const {
    data: masterTemplateData,
    // isError: isGetMasterTemplatesError,
    // isFetching: isGetMasterTemplatesFetching,
    isLoading: isGetMasterTemplatesLoading
  } = useGetMasterTemplatesQuery(
    { archived: false, condensed: false },
    {
      refetchOnMountOrArgChange: 300
    }
  );
  const {
    data: questionsData,
    // isError: isQuestionsError,
    // isFetching: isQuestionsFetching,
    isLoading: isQuestionsLoading
  } = useGetQuestionsQuery(undefined, {
    refetchOnMountOrArgChange: 300
  });

  const { templateConfigId = 'all' } = useParams();

  const handleClickTitle = (id?: string) => {
    if (id) {
      handleEditQuestion(id);
    }
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Title':
        key = 'questionTitle';
        break;
      case 'Type':
        key = 'roundingType';
        break;
      case 'Archived':
        key = 'archived';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const filteredQuestions = useMemo(() => {
    return cleanQuestions.filter(
      (o: any) =>
        o.questionTitle
          ?.toLowerCase()
          .includes(debouncedQuestionSearchTerm.toLowerCase()) ||
        o.questionParts
          ?.toLowerCase()
          .includes(debouncedQuestionSearchTerm.toLowerCase()) ||
        o.questionTags
          ?.toLowerCase()
          .includes(debouncedQuestionSearchTerm.toLowerCase())
    );
  }, [cleanQuestions, debouncedQuestionSearchTerm]);

  const sortedQuestions = useMemo(() => {
    const stateCopy = [...filteredQuestions];
    const sorted = stateCopy.sort((a: CleanQuestion, b: CleanQuestion) => {
      if (a[sortKey] === b[sortKey]) {
        return 0;
      }
      if (!a[sortKey]) {
        return sortDir === 'asc' ? 1 : -1;
      }
      if (!b[sortKey]) {
        return sortDir === 'asc' ? -1 : 1;
      }
      if (sortDir === 'asc') {
        return a[sortKey]! > b[sortKey]! ? 1 : -1;
      }
      return a[sortKey]! > b[sortKey]! ? -1 : 1;
    });

    return sorted;
  }, [filteredQuestions, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => !debouncedQuestionSearchTerm && sortedQuestions.length === 0,
    [debouncedQuestionSearchTerm, sortedQuestions]
  );

  const isTableEmptySearch = useMemo(
    () => !!debouncedQuestionSearchTerm && sortedQuestions.length === 0,
    [debouncedQuestionSearchTerm, sortedQuestions]
  );

  useEffect(() => {
    let questionsToList: Question[] = [];

    if (templateConfigId === 'all') {
      questionsToList = questionsData ?? [];
    } else {
      const foundMasterTemplate = (masterTemplateData ?? []).find(
        (template: ICondensedMasterTemplate) =>
          template.templateConfigId === templateConfigId
      );

      questionsToList =
        (foundMasterTemplate?.questions
          .map((question: ICondensedQuestion) => {
            const foundQuestion = questionsData?.find(
              (q: Question) => q.questionId === question.id
            );
            return foundQuestion;
          })
          .filter(
            (question: Question | undefined) => question
          ) as Question[]) ?? [];
    }

    const cleanData = questionsToList.map((question: Question) => {
      const questionParts = question?.questionParts.map(
        (part: QuestionPartProp) => {
          return part.question.replace(/"/g, '""').replace(/[\u2028]/g, ' ');
        }
      );
      const answers = question?.questionParts.map((part: QuestionPartProp) => {
        return part.options.flat();
      });
      const ans = [
        ...new Set(
          answers
            .flat()
            .map((opt: QuestionOptionProp) => opt.value?.replace(/"/g, ''))
        )
      ];

      return {
        questionTitle: toTitleCase(question?.questionTitle),
        questionParts: questionParts ? questionParts.join(' - ') : '',
        answers: ans.join('; '),
        questionTags: question?.questionTags?.join(', '),
        roundingType: formatRoundingType(question?.roundingType ?? ''),
        archived: question?.archived,
        questionId: question?.questionId
      };
    });

    setCleanQuestions(cleanData);
  }, [questionsData, templateConfigId]);

  useEffect(() => {
    setCsvData(sortedQuestions);
  }, [sortedQuestions]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Title"
      id="Question-Bank-Table-Title-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Title') ? 'none' : sortDir}
      sort
    >
      Title
    </CellContainer>,
    <CellContainer
      key="Parts"
      id="Question-Bank-Table-Parts-Column"
      unit="column"
      colWidth={30}
      direction="none"
    >
      Parts
    </CellContainer>,
    <CellContainer
      key="Answers"
      id="Question-Bank-Table-Answers-Column"
      unit="column"
      colWidth={15}
      direction="none"
    >
      Answers
    </CellContainer>,
    <CellContainer
      key="Tags"
      id="Question-Bank-Table-Tags-Column"
      unit="column"
      colWidth={15}
      direction="none"
    >
      Tags
    </CellContainer>,
    <CellContainer
      key="Type"
      id="Question-Bank-Table-Type-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Type') ? 'none' : sortDir}
      sort
    >
      Type
    </CellContainer>,
    <CellContainer
      key="Archived"
      id="Question-Bank-Table-Archived-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Archived') ? 'none' : sortDir}
      sort
    >
      Archived
    </CellContainer>
  ];

  const rows = sortedQuestions?.map((question: CleanQuestion, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Encounter-Row-${i}`}
        id={`Encounter-Row-${question.questionId}`}
        unit="row"
        size="default"
        height="5.5em"
      >
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel
            style={{ cursor: 'pointer', whiteSpace: 'normal' }}
            className={isEnterpriseAdmin ? 'neu-text-blue-60 bold' : ''}
            onClick={
              isEnterpriseAdmin
                ? () => handleClickTitle(question.questionId)
                : () => {}
            }
          >
            {question.questionTitle}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={30}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>
              {question.questionParts
                .replace(/[\u2028]/g, ' ')
                .replace(/""/g, '"')}
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>{question.answers}</div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            {question.questionTags}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>
            {question.roundingType
              ? formatRoundingType(question.roundingType)
              : 'Unknown'}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>
            {toTitleCase(question.archived?.toString())}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableEmptySearch}
      loading={isGetMasterTemplatesLoading || isQuestionsLoading}
      type="questionBank"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default QuestionBankTable;
